import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import { cn } from '@repo/ui/src/lib/utils';
import type {
  AnalyzedRow, ImportFieldMapping, ImportPayerMode, StudentImportAnalysis,
} from '@repo/shared-types';
import { useAnalyzeStudentImport, useCommitStudentImport } from '../../../hooks/useStudentImport';
import { UploadStep } from './steps/UploadStep';
import { MappingStep } from './steps/MappingStep';
import { PreviewStep } from './steps/PreviewStep';
import { ResultStep } from './steps/ResultStep';
import { buildCommitRows, includedRows, type ImportClassContext, type RowEditState } from './types';

type WizardStep = 'upload' | 'mapping' | 'preview' | 'result';

interface ImportWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** THI-267 Task 13 — set only by `AddMembersDialog`'s "Nhập từ Excel" tab.
   *  When present: the commit sends `classId`/`startDate`/`pricing`, Xem
   *  trước gains a Học phí column and "Dùng học viên đã có" on dup rows, and
   *  Xem trước IS the confirm step (unchanged — this wizard never had a
   *  separate confirm step to begin with). */
  classContext?: ImportClassContext | null;
  /** THI-267 Task 13 — when true, renders bare (no `Dialog`/`DialogContent`
   *  chrome of its own): `AddMembersDialog` supplies the Dialog/Drawer and
   *  title, this just fills the tab's content area. `open` then only gates
   *  whether the wizard is mounted at all (the parent controls that by
   *  mounting/unmounting the tab), not a nested dialog's visibility. */
  embedded?: boolean;
}

function seedRowEdits(rows: AnalyzedRow[]): Record<number, RowEditState> {
  const next: Record<number, RowEditState> = {};
  for (const row of rows) {
    next[row.rowNum] = {
      studentName: row.mapped.studentName,
      payerName: row.mapped.payerName ?? '',
      payerPhone: row.mapped.payerPhone ?? '',
      excluded: false,
      kept: false,
      rate: '',
      useExisting: true,
    };
  }
  return next;
}

export function ImportWizard({ open, onOpenChange, classContext = null, embedded = false }: ImportWizardProps) {
  const { t } = useTranslation('studentImport');
  const [step, setStep] = useState<WizardStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<StudentImportAnalysis | null>(null);
  const [rowEdits, setRowEdits] = useState<Record<number, RowEditState>>({});
  const [committedWarnings, setCommittedWarnings] = useState(0);
  // Fix wave (review I14) — how many rows the OPERATOR excluded client-side
  // (needs_fix "Bỏ" switch, or a same-batch duplicate left on its "bỏ qua"
  // default) never reach the server at all, so `result.skipped` (= server
  // `errors.length` only) undercounts what "N dòng bỏ qua" claims. Captured
  // at commit time so `ResultStep` can fold it into that count.
  const [excludedAtCommit, setExcludedAtCommit] = useState(0);

  const analyzeMut = useAnalyzeStudentImport();
  const commitMut = useCommitStudentImport();

  const reset = useCallback(() => {
    setStep('upload');
    setFile(null);
    setAnalysis(null);
    setRowEdits({});
    setCommittedWarnings(0);
    setExcludedAtCommit(0);
    analyzeMut.reset();
    commitMut.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleAnalyze = (selected: File) => {
    setFile(selected);
    analyzeMut.mutate(
      { file: selected },
      {
        onSuccess: (data) => {
          setAnalysis(data);
          setRowEdits(seedRowEdits(data.rows));
          setStep('mapping');
        },
      },
    );
  };

  const handleReanalyze = (override: {
    headerRowIndex: number;
    payerMode: ImportPayerMode;
    mapping: ImportFieldMapping;
  }) => {
    if (!file) return;
    analyzeMut.mutate(
      { file, override },
      {
        onSuccess: (data) => {
          setAnalysis(data);
          setRowEdits(seedRowEdits(data.rows));
        },
      },
    );
  };

  // Edited-mapping path: re-classify with the override, then go to preview.
  const handleApplyAndNext = (override: {
    headerRowIndex: number;
    payerMode: ImportPayerMode;
    mapping: ImportFieldMapping;
  }) => {
    if (!file) return;
    analyzeMut.mutate(
      { file, override },
      {
        onSuccess: (data) => {
          setAnalysis(data);
          setRowEdits(seedRowEdits(data.rows));
          setStep('preview');
        },
      },
    );
  };

  const handleCommit = () => {
    if (!analysis) return;
    // Derived once, identically to the preview count — see buildCommitRows.
    const included = includedRows(analysis, rowEdits, classContext);
    const students = buildCommitRows(analysis, rowEdits, classContext);
    if (students.length === 0) return;
    commitMut.mutate(
      classContext
        ? { classId: classContext.classId, startDate: classContext.startDate, pricing: classContext.pricing, students }
        : { students },
      {
        onSuccess: () => {
          // Count every row that will actually be committed with an issue
          // attached — not just status === 'warning'. A kept dup_warning row
          // or a repaired needs_fix row can still import with its birth date
          // dropped, and the result screen must not miss that.
          setCommittedWarnings(included.filter((r) => r.issues.length > 0).length);
          // Fix wave (I14): every analyzed row that did NOT make it into
          // `included` — operator-excluded needs_fix rows, a same-batch
          // duplicate left on "bỏ qua" — for the result screen's own
          // "N dòng bỏ qua" to fold in, since the server only ever sees
          // (and only ever counts) the rows that were actually sent.
          setExcludedAtCommit(analysis.rows.length - included.length);
          setStep('result');
        },
      },
    );
  };

  const steps: { key: WizardStep; label: string }[] = [
    { key: 'upload', label: t('steps.upload') },
    { key: 'mapping', label: t('steps.mapping') },
    { key: 'preview', label: t('steps.preview') },
    { key: 'result', label: t('steps.result') },
  ];
  const stepIndex = steps.findIndex((s) => s.key === step);

  const body = (
    <>
      <div className="flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-center gap-2 flex-1 last:flex-none">
            <div
              className={cn(
                'flex items-center gap-1.5 text-xs font-medium whitespace-nowrap',
                i === stepIndex ? 'text-brand-700' : i < stepIndex ? 'text-surface-500' : 'text-surface-300',
              )}
            >
              <span
                className={cn(
                  'flex items-center justify-center w-5 h-5 rounded-full border text-[11px] shrink-0',
                  i === stepIndex
                    ? 'bg-brand-500 border-brand-500 text-white'
                    : i < stepIndex
                      ? 'bg-surface-200 border-surface-200 text-surface-600'
                      : 'border-surface-200',
                )}
              >
                {i + 1}
              </span>
              {s.label}
            </div>
            {i < steps.length - 1 && <div className="h-px flex-1 bg-surface-200" />}
          </div>
        ))}
      </div>

      {step === 'upload' && (
        <UploadStep onAnalyze={handleAnalyze} isPending={analyzeMut.isPending} error={analyzeMut.error} />
      )}
      {step === 'mapping' && analysis && (
        <MappingStep
          analysis={analysis}
          onReanalyze={handleReanalyze}
          onApplyAndNext={handleApplyAndNext}
          onNext={() => setStep('preview')}
          isPending={analyzeMut.isPending}
          error={analyzeMut.error}
        />
      )}
      {step === 'preview' && analysis && (
        <PreviewStep
          analysis={analysis}
          rowEdits={rowEdits}
          onChange={setRowEdits}
          onBack={() => setStep('mapping')}
          onCommit={handleCommit}
          isPending={commitMut.isPending}
          error={commitMut.error}
          classContext={classContext}
        />
      )}
      {step === 'result' && commitMut.data && (
        <ResultStep
          result={commitMut.data}
          warningCount={committedWarnings}
          excludedCount={excludedAtCommit}
          onClose={() => handleOpenChange(false)}
          classContext={classContext}
        />
      )}
    </>
  );

  if (embedded) return <div className="flex flex-col gap-4">{body}</div>;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Height and scrolling now come from DialogContent itself; only the
          width is this wizard's business. */}
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('subtitle')}</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
